import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { request } from '../../../Redux/Sagas/requests/api'
import { primary_color } from '../../../Helper/uiHelper'
import { AvField, AvForm } from 'availity-reactstrap-validation'
import Select from '../../../CMSComponent/Forms/Fields/select'
import { Col, Row } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { isSuccessResponse } from '../../../helper'
import ReservedLockerTable from './reservedLockerTable'
import ChangeLocker from './changeLocker'
import { clearCurrentRowData, clearParentTableData, fetchUpdateApiData } from '../../../Redux/Reducers/CMS'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'

const blockAvailable = (item) => {
  if (typeof item?.available_items === 'number') return Math.max(0, item.available_items)
  const total = Number(item?.total_items) || 0
  const booked = Number(item?.total_booked_items) || 0
  return Math.max(0, total - booked)
}

const PupilPremiumForm = ({ addDataDetail, setAddGridDataDetail }) => {
  const [uuid, setuuid] = useState(uuidv4())
  const dispatch = useDispatch()
  const currentRow = useSelector((state) => state.CMS.currentRow)
  const [form, setForm] = useState()
  const [blocks, setBlocks] = useState([])
  const [previewLocker, setPreviewLocker] = useState([])
  const [changeLocker, setChangeLocker] = useState()
  const [noBlockError, setNoBlockError] = useState(false)
  const [tierData, setTierData] = useState([])
  const [loader, setLoader] = useState({ form: false, blocks: false, preview: false, book: false })
  const [blockErrors, setBlockErrors] = useState({})

  const [formFieldValue, setFormFieldValue] = useState({})

  const applyAvailabilityToBlocks = (availability, failedLocationId = null) => {
    if (!Array.isArray(availability) || !availability.length) return
    const byId = {}
    availability.forEach((row) => {
      byId[row.location_id] = row
    })
    const errors = {}
    setBlocks((prev) =>
      (prev || []).map((block) => {
        const updated = byId[block.location_id]
        if (!updated) return block
        const available = updated.available_items ?? blockAvailable(updated)
        const countNum = parseInt(block.count, 10) || 0
        if (countNum > available || block.location_id === failedLocationId) {
          errors[block.location_id] = true
        }
        return {
          ...block,
          ...updated,
          available_items: available,
          total_items: updated.total_items,
          total_booked_items: updated.total_booked_items,
          count: countNum > available ? String(available || '') : block.count,
        }
      })
    )
    setBlockErrors(errors)
  }

  const loadBlocks = () => {
    if (!formFieldValue?.item_date_time_id || !formFieldValue?.categories_id || !tierData?.length) return
    setNoBlockError(false)
    setLoader((l) => ({ ...l, blocks: true }))
    request('get-location-item-count', 'POST', {
      item_date_time_id: formFieldValue?.item_date_time_id,
      category_id: formFieldValue?.categories_id,
      domain_id: currentRow?.[0]?.id,
      tier: tierData?.filter((item) => item.checked)?.map((item) => item?.value),
    }).then((res) => {
      if (res.status === 200 || res.status === 202) {
        const filteredBlock = (res?.data || [])
          .map((item) => ({
            ...item,
            available_items: blockAvailable(item),
          }))
          .filter((item) => item?.total_items)
        setBlocks(filteredBlock)
        setBlockErrors({})
      } else if (res.status === 404) {
        setNoBlockError(true)
        setBlocks([])
        setPreviewLocker([])
      }
      setLoader((l) => ({ ...l, blocks: false }))
    })
  }

  useEffect(() => {
    setLoader({ ...loader, form: true })
    request('get-grid-form', 'POST', { grid_id: addDataDetail?.mainGrid?.id }).then((res) => {
      if (res.status === 200 || res.status === 202) {
        setForm(res.data?.[0])
      }
      setLoader({ ...loader, form: false })
    })
  }, [])

  useEffect(() => {
    if (formFieldValue?.item_date_time_id && formFieldValue?.categories_id) {
      setNoBlockError(false)
      setLoader({ ...loader, blocks: true })
      request('get-tier-by-category-academic', 'POST', {
        item_date_time_id: formFieldValue?.item_date_time_id,
        category_id: formFieldValue?.categories_id,
        domain_id: currentRow?.[0]?.id,
      }).then((res) => {
        let maxTier = 0
        if (res?.data?.length === 0) {
          setNoBlockError(true)
        }
        res?.data?.map((item) => {
          if (item?.max_tier > maxTier) {
            maxTier = item?.max_tier
          }
        })
        const nextTier = []
        for (let i = 1; i <= maxTier; i++) {
          nextTier?.push({
            label: `Tier ${i}`,
            value: i,
            checked: true,
          })
        }
        setTierData(nextTier)
        setLoader({ ...loader, blocks: false })
      })
    }
  }, [formFieldValue?.item_date_time_id, formFieldValue?.categories_id])

  useEffect(() => {
    loadBlocks()
  }, [formFieldValue?.item_date_time_id, formFieldValue?.categories_id, tierData])

  useEffect(() => {
    if (formFieldValue?.user_id) {
      request('get-user-by-id', 'POST', { user_id: formFieldValue?.user_id }).then((res) => {
        if (res?.data) {
          setFormFieldValue((data) => {
            return {
              ...data,
              first_name: res?.data?.first_name,
              last_name: res?.data?.surname,
            }
          })
        }
      })
    }
  }, [formFieldValue?.user_id])

  const onCloseForm = () => {
    dispatch(fetchUpdateApiData())
    if (!addDataDetail?.isAddNew) {
      dispatch(clearCurrentRowData())
      dispatch(clearParentTableData())
    }
    setAddGridDataDetail({ open: false })
    dispatch(fetchUpdateApiData())
  }

  const validateAllFields = (values) => {
    if (!values.item_date_time_id) {
      dispatch(setNotification({ type: 'error', message: 'Please select academic year' }))
      return false
    }
    if (!values.categories_id) {
      dispatch(setNotification({ type: 'error', message: 'Please select student year group' }))
      return false
    }
    if (!values.first_name) {
      dispatch(setNotification({ type: 'error', message: 'Please select name' }))
      return false
    }
    if (!values.user_id) {
      dispatch(setNotification({ type: 'error', message: 'Please select user' }))
      return false
    }
    return true
  }

  const buildBookedPayload = () => {
    const bookedPayload = []
    const errors = {}
    let exceeded = false
    blocks?.forEach((item) => {
      if (!item?.count) return
      const available = blockAvailable(item)
      const countNum = parseInt(item.count, 10) || 0
      if (countNum > available) {
        exceeded = true
        errors[item.location_id] = true
        return
      }
      bookedPayload.push({ id: item.location_id, count: countNum })
    })
    setBlockErrors(errors)
    if (exceeded) {
      dispatch(setNotification({ message: 'Number of lockers is more than available', type: 'error' }))
      return null
    }
    if (bookedPayload.length === 0) {
      dispatch(setNotification({ type: 'error', message: 'Please add locker count' }))
      return null
    }
    return bookedPayload
  }

  const onPreviewLocker = (save) => {
    if (!validateAllFields(formFieldValue)) return
    if (previewLocker?.length > 0) {
      callBookLocker(previewLocker)
      return
    }

    const bookedPayload = buildBookedPayload()
    if (!bookedPayload) return

    setLoader({ ...loader, preview: true })
    request('reserve-pupil-locker', 'POST', {
      domain_id: currentRow?.[0]?.id,
      ...formFieldValue,
      booked_locker: bookedPayload,
      session_id: uuid,
      tier: tierData?.filter((item) => item.checked)?.map((item) => item?.value),
    }).then((res) => {
      if (res?.status === 200 || res?.status === 202) {
        const reserved = res?.data?.data || []
        if (!reserved.length) {
          dispatch(
            setNotification({
              type: 'error',
              message: res?.data?.message || 'No free lockers available to reserve.',
            })
          )
          applyAvailabilityToBlocks(res?.data?.availability, res?.data?.failed_location_id)
        } else {
          setPreviewLocker(reserved)
          setBlockErrors({})
          if (save) {
            callBookLocker(reserved)
          } else {
            dispatch(
              setNotification({
                type: 'success',
                message: 'Lockers reserved until midnight. Save to confirm the booking.',
              })
            )
          }
        }
      } else {
        dispatch(
          setNotification({
            type: 'error',
            message: res?.data?.message || 'Could not reserve lockers. Availability may have changed.',
          })
        )
        applyAvailabilityToBlocks(res?.data?.availability, res?.data?.failed_location_id)
        if (!res?.data?.availability) loadBlocks()
      }
      setLoader({ ...loader, preview: false })
    })
  }

  const callBookLocker = (reservedLockers) => {
    setLoader({ ...loader, book: true })
    const lockers = reservedLockers || previewLocker
    request('booked-pupil-locker', 'POST', {
      items: lockers,
      session_id: uuid,
    }).then((res) => {
      if (res?.status === 200 || res?.status === 202) {
        isSuccessResponse(res, 'Lockers booked successfully!')
        setLoader({ ...loader, book: false })
        onCloseForm()
        return
      }
      dispatch(
        setNotification({
          type: 'error',
          message: res?.data?.message || 'Could not save booking. Please preview again.',
        })
      )
      setPreviewLocker([])
      setuuid(uuidv4())
      loadBlocks()
      setLoader({ ...loader, book: false })
    })
  }

  const onChangeLockerInPupilPremium = (locker) => {
    const newLockerData = previewLocker?.map((item) => {
      if (item?.item_id === changeLocker?.item_id) {
        const { questions, ...rest } = locker
        return { ...item, ...rest, old_item_id: changeLocker?.item_id }
      } else {
        return item
      }
    })
    setPreviewLocker(newLockerData)
    setChangeLocker()
  }

  const onChangeTier = (checked, index) => {
    const newTierData = tierData?.map((item, i) => {
      if (index === i) {
        return {
          ...item,
          checked,
        }
      } else {
        return item
      }
    })
    setTierData(newTierData)
  }

  const academicYearField = form?.component_call_fields?.find((item) => item?.field_id === 'item_date_time_id')
  const yearGroupField = form?.component_call_fields?.find((item) => item?.field_id === 'categories_id')
  const assignGroupField = form?.component_call_fields?.find((item) => item?.field_id === 'user_id')

  let isBlockError = false
  let isCountFound = false
  blocks?.map((item) => {
    if (item?.count) {
      isCountFound = true
    }
    if (parseInt(item?.count) < 0) {
      isBlockError = true
    }
  })
  if (!isCountFound) {
    isBlockError = true
  }

  return (
    <div className='p-4'>
      <div style={{ color: primary_color }} className='body1 font-weight-bold my-4'>
        Create Pupil Premium
      </div>
      {changeLocker && (
        <ChangeLocker
          changeLocker={changeLocker}
          setChangeLocker={setChangeLocker}
          item={changeLocker}
          onChangeLockerInPupilPremium={onChangeLockerInPupilPremium}></ChangeLocker>
      )}

      <div style={{ visibility: !!changeLocker && 'hidden' }}>
        <AvForm>
          <Row>
            {academicYearField && (
              <Col md={6}>
                <Select
                  data={academicYearField}
                  id={academicYearField?.field_id}
                  formFieldValue={formFieldValue}
                  setFormFieldValue={setFormFieldValue}></Select>
              </Col>
            )}
            {yearGroupField && (
              <Col md={6}>
                <Select
                  data={yearGroupField}
                  id={yearGroupField?.field_id}
                  formFieldValue={formFieldValue}
                  setFormFieldValue={setFormFieldValue}></Select>
              </Col>
            )}
          </Row>
          {noBlockError && (
            <div className='alert alert-danger' role='alert'>
              Sorry, blocks not available!
            </div>
          )}
          {tierData?.length > 0 && <div className='my-4 body1 font-weight-bold'>Blocks</div>}

          {tierData?.length > 0 && (
            <Row>
              <Col md={12}>
                <div className='d-flex'>
                  {tierData?.map((item, i) => {
                    return (
                      <div className='my-3 mr-4' style={{ display: 'flex', gap: '15px', alignItems: 'center' }} key={i}>
                        <input
                          checked={item?.checked}
                          type='checkbox'
                          id={`tier-${i}`}
                          className='subtitle1'
                          onChange={(e) => {
                            onChangeTier(e.target.checked, i)
                          }}
                        />
                        <div>{item?.label}</div>
                      </div>
                    )
                  })}
                </div>
              </Col>

              {blocks?.map((item, index) => {
                const available = blockAvailable(item)
                const total = Number(item?.total_items) || 0
                const hasError = !!blockErrors[item.location_id]
                return (
                  <Col md={6} key={item.location_id || index}>
                    <AvField
                      name={item?.location_id + 'location'}
                      value={item?.count}
                      type='number'
                      max={available}
                      disabled={available <= 0}
                      label={`${item?.location_name} (${available} free / ${total} total)`}
                      className={hasError ? 'border-danger' : undefined}
                      style={hasError ? { borderColor: '#dc3545', boxShadow: '0 0 0 0.15rem rgba(220,53,69,.25)' } : undefined}
                      onWheel={(e) => e.target.blur()}
                      onChange={(e) => {
                        const val = e.target.value
                        const countNum = parseInt(val, 10) || 0
                        if (available <= 0) {
                          dispatch(
                            setNotification({
                              message: `${item?.location_name} has no free lockers`,
                              type: 'error',
                            })
                          )
                          setBlockErrors((prev) => ({ ...prev, [item.location_id]: true }))
                          setBlocks(JSON.parse(JSON.stringify(blocks)))
                          return
                        }
                        if (countNum > available) {
                          dispatch(
                            setNotification({
                              message: `Only ${available} free in ${item?.location_name}`,
                              type: 'error',
                            })
                          )
                          setBlockErrors((prev) => ({ ...prev, [item.location_id]: true }))
                          setBlocks(JSON.parse(JSON.stringify(blocks)))
                          return
                        }
                        const newBlocks = blocks ? JSON.parse(JSON.stringify(blocks)) : []
                        newBlocks[index]['count'] = val
                        setBlocks(newBlocks)
                        setBlockErrors((prev) => {
                          const next = { ...prev }
                          delete next[item.location_id]
                          return next
                        })
                      }}></AvField>
                    {available <= 0 && (
                      <div className='text-muted small mb-2' style={{ marginTop: '-0.5rem' }}>
                        No free lockers in this block
                      </div>
                    )}
                    {hasError && available > 0 && (
                      <div className='text-danger small mb-2' style={{ marginTop: '-0.5rem' }}>
                        Available updated — only {available} free
                      </div>
                    )}
                  </Col>
                )
              })}
            </Row>
          )}
          {blocks?.length > 0 && (
            <>
              <div className='my-4 body1 font-weight-bold'>Assign</div>
              <Row>
                <Col md={12}>
                  {assignGroupField && (
                    <Select
                      data={assignGroupField}
                      id={assignGroupField?.field_id}
                      formFieldValue={formFieldValue}
                      setFormFieldValue={setFormFieldValue}
                      noCache={true}></Select>
                  )}
                </Col>
                <Col md={6}>
                  <AvField
                    name='first_name'
                    label='First Name'
                    value={formFieldValue?.first_name}
                    onChange={(e) => {
                      setFormFieldValue((data) => {
                        return {
                          ...data,
                          first_name: e.target.value,
                        }
                      })
                    }}></AvField>
                </Col>
                <Col md={6}>
                  <AvField
                    name='last_name'
                    value={formFieldValue?.last_name}
                    label='Last Name'
                    onChange={(e) => {
                      setFormFieldValue((data) => {
                        return {
                          ...data,
                          last_name: e.target.value,
                        }
                      })
                    }}></AvField>
                </Col>
              </Row>
            </>
          )}

          <button
            className='btn text-white rounded-pill mt-2'
            type='button'
            onClick={() => onPreviewLocker(false)}
            disabled={noBlockError || previewLocker?.length > 0 || loader?.preview}
            style={{ background: primary_color, fontWeight: 700, width: '8rem' }}>
            {!loader?.preview ? 'Preview' : 'loading....'}
          </button>
          <div className='my-5' style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <input
              checked={formFieldValue?.send_confirmation_mail}
              type='checkbox'
              id='emailCheckBox'
              className='subtitle1'
              onChange={(e) => {
                setFormFieldValue({ ...formFieldValue, send_confirmation_mail: e.target.checked })
              }}
            />
            <label htmlFor='emailCheckBox' className='font-weight-bold mt-2 subtitle1' style={{ opacity: 0.7 }}>
              Send Confirmation Mail
            </label>
          </div>
          {previewLocker?.length > 0 && (
            <ReservedLockerTable
              previewLocker={previewLocker}
              setPreviewLocker={setPreviewLocker}
              setChangeLocker={setChangeLocker}></ReservedLockerTable>
          )}

          <div className='my-5 d-flex justify-content-end'>
            <button
              className='btn rounded-pill mt-2 text-danger border-danger mr-3'
              type='button'
              id='close-btn'
              onClick={() => {
                onCloseForm()
              }}
              style={{ fontWeight: 700, width: '8rem' }}>
              Cancel
            </button>
            <button
              className='btn text-white rounded-pill mt-2'
              type='button'
              disabled={noBlockError || isBlockError || loader?.book}
              onClick={() => onPreviewLocker(true)}
              style={{ background: primary_color, fontWeight: 700, width: '8rem' }}>
              {!loader?.book ? 'Save' : 'Saving...'}
            </button>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default PupilPremiumForm
